import { useCallback, useState } from 'react';
import type { ArtLibraryItem } from '@volley/crucible-ipc-types';
import { useCrucibleClient } from '../../transport/context.js';
import { useDeployStatus } from '../../hooks/useDeployStatus.js';
import { DeployStatusCard, deployStatusHasContent } from '../../components/DeployStatusCard.js';

/**
 * The Art tab Library's per-asset inspector (crucible#2611 Task 5) — the
 * revision loop's UI: browse an asset's take history (click a thumbnail to
 * swap the active take), ask for a change with free-text feedback (redo:
 * un-approve + regenerate as one action), or approve the current take —
 * which auto-exports it to the game repo's `apps/display` tree so Vite's
 * dev-server HMR reloads the live Preview.
 *
 * Every mutating action calls `onChanged` on success so the LIBRARY refetches
 * `art:library` and this inspector's props update with the fresh state (a new
 * take, a swapped active index, an approved status) — this component holds no
 * "is it approved now" state of its own beyond the busy/error UI feedback.
 */

/**
 * A `data:video/...` URI is the only structured signal that distinguishes a
 * VIDEO-type animation take (Kling/Veo mp4 output) from an image-type one
 * (a loop/sprite-sheet animation, still rendered via `<img>`) — there is no
 * separate `kind`/`type` field on `ArtLibraryItem`/`ArtLibraryTake` for this
 * (crucible#2669), so this parses the data URL's own mime prefix, the same
 * structural signal the browser itself needs to decode the bytes.
 */
function isVideoDataUrl(url: string | null): boolean {
  return url !== null && url.startsWith('data:video/');
}

export interface ArtAssetInspectorProps {
  gameId: string;
  item: ArtLibraryItem;
  /** Fires after any mutating action succeeds, so the Library can refetch
   *  `art:library` and pass this inspector a fresh `item`. */
  onChanged: () => void;
}

export function ArtAssetInspector({
  gameId,
  item,
  onChanged,
}: ArtAssetInspectorProps): React.JSX.Element {
  const client = useCrucibleClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [feedback, setFeedback] = useState('');
  // crucible#2712 part 1 — the same ambient deploy-status poller Workspace's
  // top band and Dashboard's status panel already subscribe to. An Art tab
  // revision's auto-commit+push (`art:export` → `commitAndPushArtExport`,
  // art-commit.ts) triggers the SAME game repo push-triggered
  // `crucible-deploy.yml` this poller already watches.
  //
  // `subscribeOnly: true` (crucible#3502 Bugbot HIGH) — `ArtAssetInspector`
  // is ALWAYS mounted nested inside `Workspace` (via `ArtWorkspace`), and
  // `Workspace` already calls this SAME hook, start/stop-owning, for the
  // SAME gameId. Without `subscribeOnly`, deselecting an asset or leaving
  // the Art tab unmounts this inspector, which called `deploy:watch:stop`
  // even though Workspace is still mounted and still wants the watcher
  // running — freezing Workspace's own deploy card mid-pipeline. This
  // instance only listens; Workspace owns the lifecycle.
  const deployStatus = useDeployStatus(gameId, { subscribeOnly: true });

  const previewUrl = item.approvedDataUrl ?? item.takes[item.activeTakeIndex]?.dataUrl ?? null;
  const approved = item.status === 'approved';
  const hasTake = item.takes.length > 0;

  const onSwapTake = useCallback(
    async (historyIndex: number) => {
      if (historyIndex === item.activeTakeIndex) return;
      setBusy(true);
      setError(null);
      try {
        await client.invoke('art:swap-take', { id: item.id, historyIndex, gameId });
        onChanged();
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setBusy(false);
      }
    },
    [client, gameId, item.id, item.activeTakeIndex, onChanged],
  );

  const generate = useCallback(
    async (generationFeedback: string) => {
      setBusy(true);
      setError(null);
      try {
        const result = await client.invoke('art:redo', {
          gameId,
          id: item.id,
          feedback: generationFeedback,
        });
        if (!result.ok) {
          setError(result.error);
          return;
        }
        if (generationFeedback) setFeedback('');
        onChanged();
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setBusy(false);
      }
    },
    [client, gameId, item.id, onChanged],
  );

  const onRedo = useCallback(async () => {
    const trimmed = feedback.trim();
    if (!trimmed) return;
    await generate(trimmed);
  }, [feedback, generate]);

  const onApprove = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      await client.invoke('art:approve', { id: item.id, gameId });
      // The approve above already landed in the engine's manifest — refetch
      // unconditionally so the Library/inspector reflect it even if the
      // export leg below fails or throws (crucible#2611 Bugbot round 7:
      // onChanged used to be gated on export succeeding, so a successful
      // approve + failed export left status/badges/the Approve button all
      // showing the stale pre-approve snapshot, desynced from the engine,
      // with only an error string as any signal something was wrong).
      onChanged();
      const exportResult = await client.invoke('art:export', {
        gameId,
        ids: [item.id],
      });
      if (!exportResult.ok) {
        setError(`Approved, but export failed: ${exportResult.error}`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }, [client, gameId, item.id, onChanged]);

  return (
    <div className="flex flex-col gap-4" data-testid="art-asset-inspector" data-asset-id={item.id}>
      <div>
        <h3 className="truncate text-sm font-semibold text-white/90">{item.id}</h3>
        <p className="text-xs text-white/50">
          {item.category} · {item.status}
        </p>
      </div>

      {/* Current preview */}
      <div
        className="relative aspect-video w-full overflow-hidden rounded-lg bg-white/5"
        data-testid="art-asset-inspector-preview"
      >
        {item.category === 'audio' ? (
          previewUrl ? (
            // A reviewer must be able to LISTEN to an audio take, not just
            // see a glyph (crucible#2782) — `key={previewUrl}` forces a fresh
            // <audio> element (not just a src-attribute mutation) whenever
            // the active take changes (take-swap or a landed redo), so
            // playback position/paused-state never carries over from the
            // PRIOR take's element.
            <div className="flex h-full w-full flex-col items-center justify-center gap-3 p-4">
              <span aria-hidden="true" className="text-3xl">
                ♪
              </span>
              <audio
                key={previewUrl}
                controls
                src={previewUrl}
                data-testid="art-asset-inspector-audio"
                className="w-full max-w-[280px]"
              >
                Your browser does not support the audio element.
              </audio>
            </div>
          ) : (
            <div className="flex h-full w-full items-center justify-center text-xs uppercase tracking-wide text-white/40">
              ♪ audio
            </div>
          )
        ) : isVideoDataUrl(previewUrl) ? (
          // A video-type animation take (Kling/Veo mp4) — `controls` so a
          // reviewer can scrub/watch it, matching the review loop's actual
          // job (judge the take), not just confirm something rendered.
          <video
            key={previewUrl}
            controls
            loop
            muted
            src={previewUrl ?? undefined}
            data-testid="art-asset-inspector-video"
            className="h-full w-full object-contain"
          >
            Your browser does not support the video element.
          </video>
        ) : previewUrl ? (
          <img
            src={previewUrl}
            alt={item.id}
            className="h-full w-full object-contain"
            style={{
              backgroundImage:
                'linear-gradient(45deg,#222 25%,transparent 25%),linear-gradient(-45deg,#222 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#222 75%),linear-gradient(-45deg,transparent 75%,#222 75%)',
              backgroundSize: '16px 16px',
              backgroundPosition: '0 0,0 8px,8px -8px,-8px 0',
            }}
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-xs uppercase tracking-wide text-white/40">
            no preview
          </div>
        )}
        {busy && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/50">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-white/30 border-t-crucible-accent" />
          </div>
        )}
      </div>

      {/*
       * Takes strip (crucible#2669) — animation take-swap is enabled now
       * that the vendored engine (0.1.13+, Volley-Inc/asset-forge#43/#44)
       * exposes `POST /api/animations/swap-generation`, a route it lacked
       * when this strip was first gated off for animations (crucible#2611
       * Bugbot MEDIUM). `onSwapTake` still calls the same `art:swap-take`
       * channel unconditionally (crucible#3007 — rewired off the
       * cloud-unreachable `asset-review:swap-take`) — the desktop proxy
       * routes it to the asset or animation endpoint based on the manifest's
       * own category lookup (asset-review-proxy.ts's `swapTake`), so this
       * component never needs to know which endpoint fires.
       */}
      {item.takes.length > 0 && (
        <div>
          <p className="mb-1.5 text-[11px] uppercase tracking-wide text-white/40">
            Takes ({item.takes.length})
          </p>
          <div
            className="flex gap-1.5 overflow-x-auto pb-1"
            data-testid="art-asset-inspector-takes"
          >
            {item.takes.map((take) => (
              <button
                key={take.index}
                type="button"
                disabled={busy}
                onClick={() => void onSwapTake(take.index)}
                data-testid={`art-asset-inspector-take-${take.index}`}
                data-active={take.index === item.activeTakeIndex}
                title={`${take.model} · ${take.timestamp}`}
                className={`h-14 w-14 shrink-0 overflow-hidden rounded-md border-2 transition disabled:opacity-40 ${
                  take.index === item.activeTakeIndex
                    ? 'border-crucible-accent'
                    : 'border-white/10 hover:border-white/30'
                }`}
              >
                {/*
                 * Branch order matters: video takes (crucible#2669) resolve to
                 * `data:video/...` and get a silent looping <video> thumbnail;
                 * audio takes (crucible#2782) resolve to `data:audio/...` and
                 * must hit the glyph BEFORE the <img> branch, or the strip
                 * would render a broken <img src="data:audio/...">. A silent
                 * controls-free loop reads as a "this take is a clip"
                 * indicator; the big preview above is where a reviewer watches
                 * it with controls.
                 */}
                {isVideoDataUrl(take.dataUrl) ? (
                  <video
                    src={take.dataUrl ?? undefined}
                    className="h-full w-full object-cover"
                    autoPlay
                    loop
                    muted
                    playsInline
                  />
                ) : item.category !== 'audio' && take.dataUrl ? (
                  <img
                    src={take.dataUrl}
                    alt={`take ${take.index}`}
                    className="h-full w-full object-cover"
                  />
                ) : item.category === 'audio' ? (
                  <div className="flex h-full w-full items-center justify-center bg-white/5 text-xs text-white/40">
                    ♪
                  </div>
                ) : (
                  <div className="flex h-full w-full items-center justify-center bg-white/5 text-[9px] text-white/40">
                    #{take.index}
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {error && (
        <div className="rounded-lg bg-red-500/15 p-3 text-xs text-red-300" role="alert">
          {error}
        </div>
      )}

      {hasTake ? (
        <>
          {/* Feedback → redo */}
          <div className="flex flex-col gap-2">
            <textarea
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder="e.g. warmer light and a calmer pose"
              data-testid="art-asset-inspector-feedback"
              disabled={busy}
              className="h-16 w-full resize-none rounded-lg border border-white/15 bg-black/40 p-2 text-xs text-white/90 placeholder:text-white/30 focus:border-crucible-accent focus:outline-none disabled:opacity-50"
            />
            <button
              type="button"
              disabled={busy || !feedback.trim()}
              onClick={() => void onRedo()}
              data-testid="art-asset-inspector-redo"
              className="rounded-lg bg-white/10 px-3 py-1.5 text-xs font-medium text-white/90 transition hover:bg-white/15 disabled:opacity-40"
            >
              Ask for a change
            </button>
          </div>

          {/* Approve (auto-exports on success) */}
          <button
            type="button"
            disabled={busy || approved}
            onClick={() => void onApprove()}
            data-testid="art-asset-inspector-approve"
            className="rounded-lg bg-crucible-accent px-3 py-2 text-sm font-medium text-black transition hover:brightness-110 disabled:cursor-default disabled:opacity-50"
          >
            {approved ? 'Approved' : 'Approve this take'}
          </button>

          {/* crucible#2712 part 1 — inspector-footer deploy status. Renders
              nothing for a quiet `idle` snapshot (no active pipeline for this
              game yet) — gated on the shared `deployStatusHasContent`
              predicate so this wrapper and the card's own null-return can
              never diverge, same invariant Workspace's top band relies on. */}
          {deployStatusHasContent(deployStatus) && (
            <div data-testid="art-asset-inspector-deploy-status">
              <DeployStatusCard status={deployStatus} />
            </div>
          )}
        </>
      ) : (
        <button
          type="button"
          disabled={busy}
          onClick={() => void generate('')}
          data-testid="art-asset-inspector-generate-first"
          className="rounded-lg bg-crucible-accent px-3 py-2 text-sm font-medium text-black transition hover:brightness-110 disabled:opacity-50"
        >
          {busy ? 'Generating first version…' : 'Generate first version'}
        </button>
      )}
    </div>
  );
}
